/**
 * The pieces a post row is made of, shared by the feed card and the list row so
 * a figure reads the same in both presentations.
 */

import {
  Bookmark,
  Clapperboard,
  Clock,
  ClockFading,
  Eye,
  Film,
  Globe,
  Heart,
  Image as ImageIcon,
  MessageSquare,
  Radar,
  Repeat2,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { HintedIcon } from '../../../chrome/hint';
import { Absent } from '../../../chrome/measure';
import { count, interval } from '../../../chrome/measure/format';
import { getLocale, type Locale } from '../../../i18n';
import { compact } from '../../../i18n/format';
import { Badge } from '../../../ui/badge';
import { Skeleton } from '../../../ui/skeleton';
import { cn } from '../../../ui/utils';
import { AccountAvatar, CoauthorStack } from '../media/AccountAvatar';
import { MediaCountBadge } from '../media/MediaView';
import { media as mediaMessage } from '../media/messages';
import type { PostRow } from '../api/schemas';
import { kindLabel, networkLabel, pub } from './messages';
import { collected, type PostFigures, type Reading, type Score } from './readings';

/** The two networks accounts can actually be added under (`campaign/accounts`'s
 *  `AddAccount.tsx`); an unrecognised wire value still renders, through
 *  `Globe`, rather than guessing at a third brand mark. */
function InstagramGlyph(props: { className?: string }): ReactNode {
  return (
    <svg
      aria-hidden
      className={props.className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

function TiktokGlyph(props: { className?: string }): ReactNode {
  return (
    <svg aria-hidden className={props.className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.25 1.25 2.29 2.5 2.52 1.06.2 2.21-.08 2.97-.83.67-.62.97-1.54.98-2.43.02-4.52.01-9.04.01-13.56z" />
    </svg>
  );
}

/** The network's glyph, named on hover, focus and to a screen reader with
 *  the network and the post's kind, both
 *  through the messages catalogue rather than the wire's own tokens. */
function NetworkGlyph(props: { post: PostRow }): ReactNode {
  const { platform, kind } = props.post;
  const icon =
    platform === 'instagram' ? (
      <InstagramGlyph className="size-3.5" />
    ) : platform === 'tiktok' ? (
      <TiktokGlyph className="size-3.5" />
    ) : (
      <Globe aria-hidden className="size-3.5" />
    );
  return (
    <HintedIcon
      label={`${networkLabel(platform)} · ${kindLabel(kind)}`}
      className="text-muted-foreground"
    >
      {icon}
    </HintedIcon>
  );
}

/** "há 2 h": the largest whole unit that fits, past tense, through
 *  `Intl.RelativeTimeFormat` rather than a hand-built table of thresholds. */
export function postAge(iso: string, locale: Locale): string {
  const elapsedSeconds = (Date.now() - new Date(iso).getTime()) / 1000;
  const abs = Math.abs(elapsedSeconds);
  const [divisor, unit]: [number, Intl.RelativeTimeFormatUnit] =
    abs >= 86400
      ? [86400, 'day']
      : abs >= 3600
        ? [3600, 'hour']
        : abs >= 60
          ? [60, 'minute']
          : [1, 'second'];
  const value = Math.round(elapsedSeconds / divisor);
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' }).format(
    -value,
    unit,
  );
}

export function moment(iso: string): string {
  return new Intl.DateTimeFormat(getLocale(), {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** The account's label, else its handle; a row with neither says the network
 *  did not expose one rather than rendering an empty string. */
export function PostAuthor(props: { post: PostRow }): ReactNode {
  const name = props.post.label ?? props.post.handle;
  return name === null ? <Absent reason="not_observable" /> : name;
}

export function PostAvatar(props: { post: PostRow }): ReactNode {
  const name = props.post.label ?? props.post.handle;
  return <AccountAvatar avatarUrl={props.post.avatar_url} name={name} />;
}

/** Collab authors past the main byline, as a small overlapping avatar stack. */
export function PostCoauthors(props: { post: PostRow }): ReactNode {
  return <CoauthorStack coauthors={props.post.coauthors} />;
}

/** The network's glyph, the handle (only when the label already took the name
 *  line, so it is never shown twice) and the post's relative age. */
export function PostMeta(props: { post: PostRow }): ReactNode {
  const { post } = props;
  const parts = [post.label !== null ? post.handle : null, postAge(post.published_at, getLocale())];
  return (
    <span className="text-caption text-muted-foreground inline-flex min-w-0 items-center gap-1.5">
      <NetworkGlyph post={post} />
      <span className="truncate">{parts.filter((part) => part !== null).join(' · ')}</span>
    </span>
  );
}

/** The row's content kind, visible rather than the sr-only announcement
 *  `NetworkGlyph` already carries. */
/** One glyph per content kind, keyed by the wire's kind upper-cased (it
 *  spells `STORY` or `story`); the type filter draws the same ones. */
export const KIND_ICONS: Record<string, LucideIcon> = {
  POST: ImageIcon,
  FEED_IMAGE: ImageIcon,
  REEL: Clapperboard,
  STORY: ClockFading,
};

export function KindBadge(props: { post: PostRow }): ReactNode {
  const Icon = KIND_ICONS[props.post.kind.toUpperCase()];
  return (
    <Badge variant="outline">
      {Icon !== undefined && <Icon aria-hidden />}
      {kindLabel(props.post.kind)}
    </Badge>
  );
}

export function StoryCaptured(props: { capturedAt: string }): ReactNode {
  return <span>{pub('story.capturedAt', { date: moment(props.capturedAt) })}</span>;
}

/** A chip reading "expira em 2 h", or "expirada" once past; the same unit
 *  ladder as `postAge`, run forward instead of back. */
export function StoryExpiry(props: { expiresAt: string }): ReactNode {
  const locale = getLocale();
  const elapsedSeconds = (new Date(props.expiresAt).getTime() - Date.now()) / 1000;
  if (elapsedSeconds <= 0) return <Badge variant="secondary">{pub('story.expired')}</Badge>;
  const [divisor, unit]: [number, Intl.RelativeTimeFormatUnit] =
    elapsedSeconds >= 3600 ? [3600, 'hour'] : elapsedSeconds >= 60 ? [60, 'minute'] : [1, 'second'];
  const value = Math.round(elapsedSeconds / divisor);
  const when = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' }).format(
    value,
    unit,
  );
  return (
    <Badge variant="outline">
      <Clock aria-hidden />
      {pub('story.expiresIn', { when })}
    </Badge>
  );
}

/** A story's frame at its own 9:16, letterboxed rather than cropped. A card's
 *  (`controls` unset) video never plays with controls — it shows a poster or
 *  its silent first frame, the same as any other card; the story's own page
 *  (`controls`) plays it with full controls instead. Nothing renders for a
 *  story with no media rows at all; one still pending its download shows a
 *  kind-aware placeholder rather than an empty frame. */
export function StoryFrame(props: {
  post: PostRow;
  className?: string;
  controls?: boolean;
  /** Width over height of the stored file, once it has loaded. */
  onAspect?: (ratio: number) => void;
}): ReactNode {
  const [failed, setFailed] = useState(false);
  const media = props.post.media.find((item) => item.source_url !== null) ?? props.post.media[0];
  if (media === undefined) return null;
  const pending = media.source_url === null && !failed;
  const Icon = media.media_kind === 'video' ? Film : ImageIcon;
  return (
    <div
      className={cn(
        'relative flex aspect-story flex-col items-center justify-center gap-1.5 overflow-hidden bg-black text-white/60',
        props.className,
      )}
    >
      {media.source_url === null || failed ? (
        <>
          <Icon aria-hidden className="size-6" />
          {pending && (
            <span className="text-caption px-2 text-center">
              {mediaMessage('placeholder.pending')}
            </span>
          )}
        </>
      ) : media.media_kind === 'video' ? (
        <video
          aria-label={pub('post.mediaAlt')}
          src={media.source_url}
          poster={media.poster_url ?? undefined}
          controls={props.controls ?? false}
          playsInline
          muted
          preload="metadata"
          className="absolute inset-0 size-full object-contain"
          onLoadedMetadata={(event) =>
            props.onAspect?.(event.currentTarget.videoWidth / event.currentTarget.videoHeight)
          }
          onError={() => setFailed(true)}
        />
      ) : (
        <img
          alt={pub('post.mediaAlt')}
          src={media.source_url}
          className="absolute inset-0 size-full object-contain"
          onLoad={(event) =>
            props.onAspect?.(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight)
          }
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

export function DeletedBadge(props: { post: PostRow }): ReactNode {
  if (props.post.deleted_at === null) return null;
  return (
    <Badge variant="outline" title={pub('post.deletedOn', { date: moment(props.post.deleted_at) })}>
      {pub('post.deleted')}
    </Badge>
  );
}

export function PostCaption(props: { post: PostRow }): ReactNode {
  return props.post.caption === null ? (
    <Absent reason={{ text: pub('post.noCaption') }} />
  ) : (
    props.post.caption
  );
}

/** No block at all for a post with no media rows; one still pending its
 *  download, or a source that fails to load, falls back to a kind-aware
 *  placeholder instead of an empty box. A stored video with no cover frame
 *  falls back the same way, since this card never plays video. A carousel
 *  badges its own item count, since this card shows only the first. */
export function PostThumb(props: {
  post: PostRow;
  className: string;
  onAspect?: (ratio: number) => void;
}): ReactNode {
  const [failed, setFailed] = useState(false);
  const item = props.post.media[0];
  if (item === undefined) return null;
  const image =
    item.media_kind === 'image' ? item.source_url : (item.poster_url ?? item.source_url);
  const showImage =
    item.media_kind === 'image' ? item.source_url !== null : item.poster_url !== null;
  const Icon = item.media_kind === 'video' ? Film : ImageIcon;
  return (
    <div
      className={cn(
        'bg-secondary text-muted-foreground relative flex flex-col items-center justify-center gap-1 overflow-hidden',
        props.className,
      )}
    >
      {failed || !showImage || image === null ? (
        <>
          <Icon aria-hidden className="size-4" />
          {item.pending && !failed && (
            <span className="text-caption px-1 text-center">
              {mediaMessage('placeholder.pending')}
            </span>
          )}
        </>
      ) : (
        <img
          alt={pub('post.mediaAlt')}
          src={image}
          className="absolute inset-0 size-full object-cover"
          onLoad={(event) =>
            props.onAspect?.(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight)
          }
          onError={() => setFailed(true)}
        />
      )}
      <MediaCountBadge count={props.post.media.length} />
    </div>
  );
}

/** One figure of a post, or nothing when the post has no reading of it: a
 *  metric the network does not collect for this post is left out. A returned
 *  zero renders `0`. Reach is the one figure large enough to want compacting;
 *  the others stay full counts. */
export function FigureValue(props: {
  figures: Reading<PostFigures>;
  metric: keyof PostFigures;
}): ReactNode {
  const { figures, metric } = props;
  if (figures.status === 'loading') return <Skeleton aria-hidden className="h-4 w-8" />;
  if (figures.status === 'failed') return <Absent reason="not_measured" />;
  const figure = figures.value[metric];
  if (figure === null || figure.value === null) return null;
  const locale = getLocale();
  const formatted =
    metric === 'reach' || metric === 'estimatedReach'
      ? compact(figure.value, locale)
      : count(figure.value, locale);
  return (
    <span className="inline-flex items-center gap-1">
      <span data-numeric>{formatted}</span>
      {figure.fidelity === 'ESTIMATED' && metric !== 'estimatedReach' && (
        <Badge variant="outline" className="border-dashed">
          {pub('figure.estimated')}
        </Badge>
      )}
    </span>
  );
}

const METRIC_ICON = {
  reach: Eye,
  estimatedReach: Radar,
  likes: Heart,
  comments: MessageSquare,
  shares: Repeat2,
  saves: Bookmark,
};

/** A figure behind its glyph, the glyph named on hover, focus and to a screen
 *  reader; nothing at all for a metric the post has no reading of. */
export function Metric(props: {
  figures: Reading<PostFigures>;
  metric: keyof PostFigures;
}): ReactNode {
  const { figures, metric } = props;
  if (figures.status === 'ready' && !collected(figures.value, metric)) return null;
  const Icon = METRIC_ICON[metric];
  return (
    <span className="text-caption text-muted-foreground inline-flex items-center gap-1">
      <HintedIcon label={pub(`metric.${metric}`)}>
        <Icon aria-hidden className="size-3.5" />
      </HintedIcon>
      <FigureValue figures={figures} metric={metric} />
    </span>
  );
}

/** A card names the direction of a week's score, not its research-grade
 *  fractional digit; the interval it sits beside keeps that precision. */
function wholeScore(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0, signDisplay: 'exceptZero' })
    .format(value)
    .replace(/^-/, '−');
}

/** The signed score of the latest week. Colour repeats what the sign says, it
 *  is never the only signal. */
export function SentimentChip(props: { sentiment: Reading<Score | null> }): ReactNode {
  const { sentiment } = props;
  if (sentiment.status === 'loading') return <Skeleton aria-hidden className="h-6 w-12" />;
  if (sentiment.status === 'failed' || sentiment.value === null) {
    return (
      <span className="text-caption text-foreground border-border rounded-sm border px-2 py-1 whitespace-nowrap">
        {pub('sentiment.none')}
      </span>
    );
  }
  const locale = getLocale();
  const { score: value, n, interval: range } = sentiment.value;
  const tone =
    value > 0
      ? 'bg-primary-quiet text-primary-quiet-foreground'
      : value < 0
        ? 'bg-destructive-quiet text-destructive-quiet-foreground'
        : 'bg-secondary text-foreground';
  return (
    <span className={`text-caption rounded-sm px-2 py-1 whitespace-nowrap ${tone}`}>
      <span aria-hidden data-numeric>
        {wholeScore(value, locale)}
      </span>
      <span className="sr-only">
        {pub('sentiment.label', {
          score: wholeScore(value, locale),
          n: count(n, locale),
          interval: interval(range[0], range[1], locale),
        })}
      </span>
    </span>
  );
}
